import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

// CPH's message ships as HTML (e.g. "<h1>Title</h1>Body..."). Render it as plain
// text - strip tags, don't dangerouslySetInnerHTML an upstream string.
const stripHtml = (html) =>
    (html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * CPH's own disruption notice (special_warning from the app feed, stored in the
 * DB). Dismiss is keyed to the message text, so a NEW warning shows again while
 * a dismissed one stays hidden.
 */
function DisruptionBanner({ warning }) {
    const { t } = useTranslation('common');
    const [dismissed, setDismissed] = useState(true); // hidden until we know it's not dismissed

    const message = stripHtml(warning?.message);

    useEffect(() => {
        if (!message) return;
        const prev = localStorage.getItem('disruptionDismissed');
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDismissed(prev === message);
    }, [message]);

    if (!message || dismissed) return null;

    const critical = Boolean(warning?.critical);
    const readMoreUrl = warning?.readMoreUrl;

    const handleClose = () => {
        localStorage.setItem('disruptionDismissed', message);
        setDismissed(true);
    };

    return (
        <div
            role="alert"
            className={`relative flex items-center justify-center px-8 py-2 text-center text-sm ${
                critical ? 'bg-red-600 text-white' : 'bg-yellow-300 text-black'
            }`}
        >
            <span>
                {message}
                {readMoreUrl && (
                    <a
                        href={readMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 underline"
                    >
                        {warning?.readMoreText || t('read_more')}
                    </a>
                )}
            </span>
            <button
                onClick={handleClose}
                aria-label={t('close')}
                className="absolute right-2 top-1 cursor-pointer border-none bg-transparent text-xl"
            >
                &#10005;
            </button>
        </div>
    );
}

export default DisruptionBanner;
